import type { ShopVariantDetail } from '../api/types';

/**
 * Stone as a PRICED axis (R-03, CR-43).
 *
 * It used to be descriptive: the dropdowns listed whatever Admin had marked the
 * design as "offered in", any combination was selectable, and one with no real
 * variant behind it went into the cart under the VIEWED variant's code and price
 * with the stone written into a remark. The client's own catalogue files say
 * that is wrong. Colette Pear Ring, 10K white gold, diamond colour D:
 *
 *     VVS1 1390 · VVS2 1320 · VS1 1230 · VS2 1130
 *
 * So a retailer choosing VS2 was quoted 1390 and told Miraki would confirm.
 * Stone changes the price in every file the client sent, and metal colour
 * changes it in none of them.
 *
 * What follows from that is the whole of this module: **the options are the
 * priced variants.** Not what Admin offered — what exists at a price. A
 * combination with nothing behind it is not a cheaper way of asking for a quote,
 * it is a piece Miraki does not sell, and offering it produces an order line
 * whose number is someone else's.
 *
 * Pure functions, no DOM, no server — the cheapest suite that can prove it
 * (mk-test §1).
 *
 * **This is not availability (D4).** A combination absent here is *not made*.
 * There is no stock, no back-order, and nothing on this screen may suggest that
 * choosing differently later would find one.
 */

export type StoneSibling = ShopVariantDetail['siblings'][number];
export type MetalField = 'purity' | 'metalColour';
export type StoneField =
  | 'stoneOrigin' | 'gemstone' | 'stoneShape' | 'stoneCut' | 'stoneColour' | 'stoneClarity';
/** Every axis a buyer filters on. Metal and stone differ only in where the
 *  values come from and which panel draws them. */
export type VariantField = MetalField | StoneField;
export type StoneOptions = ShopVariantDetail['stoneOptions'];
/**
 * What the retailer has ticked, by axis. **Many values per axis** (CR-46): the
 * dropdowns are the admin screen's multi-select, so a buyer says "D and E, in
 * VS1 and VS2" in one pass and gets four rows to put quantities against.
 *
 * An axis absent, or present and empty, means *no filter on that axis* — not
 * "nothing matches". That is what makes an untouched screen show every piece
 * the frame carries rather than none of them.
 */
export type StoneSelection = Partial<Record<VariantField, readonly string[]>>;

/**
 * Origin first, because it qualifies everything after it — Lab Grown or Natural
 * is the first thing a trade buyer settles, and the derived variant code leads
 * with it for the same reason. Carat is Metal's "Centre stone carat", not
 * Stone's, so it has no entry here.
 */
/**
 * Metal, in the order the client's master list presents it: purity then colour.
 * Kept separate from `STONE_FIELDS` because the two are drawn in two panels and
 * their values come from different places — metal's from the pieces themselves
 * and their admin-defined sort order, stone's from what Admin has offered.
 */
export const METAL_FIELDS: ReadonlyArray<{ key: MetalField; labelKey: string }> = [
  { key: 'purity', labelKey: 'shop.variant.purity' },
  { key: 'metalColour', labelKey: 'shop.variant.colour' },
];

export const STONE_FIELDS: ReadonlyArray<{ key: StoneField; labelKey: string }> = [
  { key: 'stoneOrigin', labelKey: 'shop.variant.stoneOrigin' },
  { key: 'gemstone', labelKey: 'shop.variant.stoneMaterial' },
  { key: 'stoneShape', labelKey: 'shop.variant.stoneShape' },
  { key: 'stoneCut', labelKey: 'shop.variant.stoneCut' },
  { key: 'stoneColour', labelKey: 'shop.variant.stoneColour' },
  { key: 'stoneClarity', labelKey: 'shop.variant.stoneClarity' },
];

export function stoneFieldValue(s: StoneSibling, key: VariantField): string | null {
  return s[key] ?? null;
}

/**
 * The metal axes this product actually varies on.
 *
 * A design made in one purity only gets no purity control — there is nothing to
 * choose between, exactly as a stone axis no piece distinguishes gets none.
 * Values come back in the admin-defined order (`mki_attribute_values.sort_order`):
 * purity ascending by fineness, colour yellow / white / rose. Sorting the labels
 * instead lands both backwards — "18k" reads as a descending karat number and
 * alphabetical colour order is the exact reverse of the real one.
 */
export function activeMetalFields(
  siblings: readonly StoneSibling[],
): ReadonlyArray<{ key: MetalField; labelKey: string; values: string[] }> {
  const sortKey: Record<MetalField, 'puritySortOrder' | 'metalColourSortOrder'> = {
    purity: 'puritySortOrder', metalColour: 'metalColourSortOrder',
  };
  return METAL_FIELDS
    .map(({ key, labelKey }) => {
      const order = new Map<string, number>();
      for (const s of siblings) {
        const v = s[key];
        const k = s[sortKey[key]];
        if (v && k !== null && k !== undefined && !order.has(v)) order.set(v, k);
      }
      const values = [...new Set(siblings.map((s) => s[key]).filter((v): v is string => !!v))]
        .sort((a, b) => (order.get(a) ?? Number.MAX_SAFE_INTEGER)
          - (order.get(b) ?? Number.MAX_SAFE_INTEGER) || a.localeCompare(b));
      return { key, labelKey, values };
    })
    .filter((d) => d.values.length > 1);
}

/**
 * A field no priced variant distinguishes gets no dropdown — there is nothing to
 * choose between. Absent entirely for a design with no stone at all, the same
 * convention every other empty section on this screen uses.
 */
export function activeStoneFields(
  options: StoneOptions,
): ReadonlyArray<{ key: StoneField; labelKey: string }> {
  return STONE_FIELDS.filter(({ key }) => (options[key]?.length ?? 0) > 0);
}

/**
 * The values this frame's pieces actually carry on one axis, in the master
 * list's order.
 *
 * Options are still **the priced variants** — the point the whole module turns
 * on — but they are no longer cascaded against each other. Under multi-select
 * a cascade is the wrong shape: narrowing Clarity because Colour is ticked
 * would pull values out from under a buyer mid-pass, and the dead end it used
 * to prevent cannot happen here. A combination with nothing behind it simply
 * contributes no row, which the table says plainly instead of a dropdown
 * silently refusing to offer it.
 */
export function stoneValuesFor(
  options: StoneOptions,
  field: StoneField,
  siblings: readonly StoneSibling[],
): string[] {
  const carried = new Set(
    siblings.map((s) => stoneFieldValue(s, field)).filter((v): v is string => v !== null),
  );
  // Master-list order, never the order variants happen to come back in.
  return (options[field] ?? []).filter((v) => carried.has(v));
}

/** True when no axis is ticked at all — the state the table asks to leave. */
export function isStoneSelectionEmpty(sel: StoneSelection): boolean {
  return [...METAL_FIELDS, ...STONE_FIELDS]
    .every(({ key }) => (sel[key]?.length ?? 0) === 0);
}

/**
 * The axes still waiting on a choice.
 *
 * "No rows" has two causes and they need different words. Either the buyer has
 * not finished choosing — say which axis is outstanding — or they have chosen
 * fully and Miraki does not make that combination. Telling someone their piece
 * is not made when they simply have not picked a clarity yet is the worse of
 * the two mistakes, and it must never read as "out of stock" either way (D4).
 */
export function unansweredFields(
  fields: ReadonlyArray<{ key: VariantField; labelKey: string }>,
  sel: StoneSelection,
  availableFor?: (key: VariantField) => number,
): ReadonlyArray<{ key: VariantField; labelKey: string }> {
  return fields.filter((f) => {
    const want = sel[f.key];
    if (want && want.length > 0) return false;
    return (availableFor?.(f.key) ?? 1) > 0;
  });
}

/**
 * The pieces the retailer has described — and **only** those.
 *
 * An axis with nothing ticked yields NO rows, rather than every value of it.
 * The table is the retailer's selection made concrete, so an empty Clarity has
 * to mean "you have not said which clarity yet", not "all four of them". Read
 * the other way round, clearing one dropdown turned a two-row table into
 * eighty, which is the wall this panel exists to avoid.
 *
 * The exception is an axis with nothing to pick. A design can carry Ruby pieces
 * that have a shape and Diamond pieces that do not, so Shape is an axis overall
 * and offers nothing once Diamond is chosen. Requiring a tick there would be a
 * dropdown holding no options that can never be satisfied, and the table would
 * stay empty for good. An axis with no available values is already answered.
 *
 * Never crosses the metal frame on its own: a stone specification is not unique
 * — Colette makes each of its 20 stones in all 9 frames — so purity and colour
 * are axes here like any other, and a table spanning frames is the buyer
 * deliberately ticking more than one.
 */
export function matchingStoneSiblings(
  siblings: readonly StoneSibling[],
  fields: ReadonlyArray<{ key: VariantField }>,
  sel: StoneSelection,
  /** Values this axis can offer at all, so "nothing to pick" is distinguishable
   *  from "nothing picked yet". Absent means every axis has something. */
  availableFor?: (key: VariantField) => number,
): StoneSibling[] {
  const answered = (key: VariantField) => {
    const want = sel[key];
    if (want && want.length > 0) return true;
    // Nothing ticked: answered only when there was nothing to tick.
    return (availableFor?.(key) ?? 1) === 0;
  };
  if (!fields.every(({ key }) => answered(key))) return [];

  return siblings.filter((s) => fields.every(({ key }) => {
    const want = sel[key];
    if (!want || want.length === 0) return true;   // the nothing-to-pick axis
    const has = stoneFieldValue(s, key);
    return has !== null && want.includes(has);
  }));
}

/**
 * Choose ONE value on an axis, and bring every other axis into line (R-03).
 *
 * Each axis holds a single value, so picking a different one replaces the old.
 * The trouble with single choice is that the other axes may not co-exist with
 * the new value — 18K may not be made with the clarity currently ticked. Refusing
 * the click would strand the buyer (they would have to change clarity first, and
 * could not know that); leaving the other axes alone would offer a piece Miraki
 * does not make. So the other axes are walked in order and each keeps its current
 * value if some real, priced variant still carries it alongside everything chosen
 * so far, and otherwise moves to the first value that does.
 *
 * The result therefore always names at least one real variant, and only ever the
 * ones that exist — an invalid combination cannot be produced from here.
 * An axis none of the remaining variants carries a value for is left out.
 */
export function selectOne(
  siblings: readonly StoneSibling[],
  fields: ReadonlyArray<{ key: VariantField }>,
  sel: StoneSelection,
  key: VariantField,
  value: string,
): StoneSelection {
  let pool = siblings.filter((s) => stoneFieldValue(s, key) === value);
  const next: StoneSelection = { [key]: [value] };
  for (const { key: k } of fields) {
    if (k === key) continue;
    const values = [...new Set(
      pool.map((s) => stoneFieldValue(s, k)).filter((v): v is string => v !== null),
    )];
    if (values.length === 0) continue;
    const current = sel[k]?.[0];
    const chosen = current !== undefined && values.includes(current) ? current : values[0]!;
    next[k] = [chosen];
    pool = pool.filter((s) => stoneFieldValue(s, k) === chosen);
  }
  return next;
}
